import {
	getSignedUrl,
	downloadFileAndShowInTab,
	downloadZip,
	downloadFileBase64,
	downloadFileBlob,
	extractUserIdFromS3Url
} from "./FilesAndS3Utils";
import {getUserLabel} from "../../../components/WorksiteTask/utils";
import _ from "lodash";
import {getDateSlug} from "../../../utils/date";
import {stringToSlug} from "../../../utils/string";
import ActionButtonComponent from "@ui-components/ActionButtonComponent";
import ArrowDownTrayIcon from "@heroicons/react/24/solid/ArrowDownTrayIcon";
export const buildTaskImagesAndReports = (worksite, tasks, taskImages, worksiteUsers, dispatchPDFDownload, intl, apiRequest) => {

	/*
	buildTaskImageInfo is a function that builds the info of a task image
	parameters: the task image
	returns a promise with a string with the info of the task image.
	A call to S3 is made to get the object attributes of the image
	a promise is returned to make so that the document is visible while the info is being fetched
	and a spinner is shown in the meantime instead of the info
	 */
	const buildTaskImageInfo = async (taskImage) => {
		const objectAttributes = await apiRequest.post('/get_object_attributes', {
			bucket: 'task_images',
			prefix: taskImage.val_url.split(".com/").pop()
		})
		const loadedOn = objectAttributes.LastModified
		const url = taskImage.val_url
		const isoStrM = loadedOn;
		const dateM = new Date(isoStrM);
		const offset = 0
		const timestampWithOffsetM = dateM.getTime();
		const timestampWithoutOffsetM = timestampWithOffsetM;
		const dateWithoutOffsetM = new Date(timestampWithoutOffsetM);

		const author_info = worksiteUsers
			?.find((u) => u.id === extractUserIdFromS3Url(url))
			?.name
		return `
		${intl.formatMessage({id: "loaded_on"})} 
		${intl.formatDate(new Date(dateWithoutOffsetM), {
			day: "numeric",
			month: "long",
			year: "numeric"
		})}
		${intl.formatMessage({id: "at"})} ${intl.formatTime(new Date(dateWithoutOffsetM))}
		${intl.formatMessage({id: "by"})} ${author_info || "-"}
			`
	}


	/*
	prepareAndDownloadZip is a function that prepares a zip file with all the documents selected
	parameters: documents
	returns a promise with the zip file
	this function makes parallel requests to download all the documents
	and then creates a zip file with them.
	Task images are downloaded from s3
	Task reports are generated from an api call to backend.
	 */
	const prepareAndDownloadZip = async (documents) => {
		const promises = documents.map(async (document) => {
			let blob = null
			let filename = null
			if (document.task) {
				blob = await downloadTaskReport(document.task, async (response) => response)
				filename = `${getDateSlug()}_${stringToSlug(document.task.des_name)}.pdf`
			} else {
				const signed_url = await getSignedUrl(apiRequest, document.s3_url, document.name, document.bucket);
				blob = await downloadFileBlob(signed_url);
				filename = document.name
			}
			return {filename: filename, blob: blob};
		})
		const files = await Promise.all(promises)
		const zipname = intl.formatMessage({id: "worksite_tasks_reports_maps_and_attachments"});
		await downloadZip(zipname, files);
	}

	/*
	downloadTaskReport is a function that downloads a task report
	parameters:
	- task: the task
	- onDownload: a function to be called when the report is downloaded
		(this has been added to handle the case when the report is opened in a new tab,
		 or added to the zip file)
	returns the report as a promise
	 */
	const downloadTaskReport = async (task, onDownload) => {
		const activities = await apiRequest.get(
			"/worksites_tasks_activities/byidtask?id_task=" + task.id
		);
		let taskmapImageData = null;
		if (task.task_map_image) {
			const taskmapImageSignedUrl = await getSignedUrl(
				apiRequest,
				task.task_map_image.val_url,
				getTaskImageName(task.task_map_image, worksitesActivityAttachmentCounter),
				'worksite_images'
			);
			taskmapImageData = await downloadFileBase64(taskmapImageSignedUrl)
		}
		const data = {
			id: task.id,
			worksite: `${worksite.address} - ${worksite.city}`,
			owner: getUserLabel(worksiteUsers?.find((u) => u.id === task.id_owner), intl, false),
			assignee: getUserLabel(worksiteUsers?.find((u) => u.id === task.id_assignee), intl, false),
			title: task.des_name,
			subtitle: intl.formatMessage({id: "worksite_task"}) + (!!task.survey?.name ? ` - ${task.survey?.name}` : ""),
			description: task.des_description,
			status: task.status,
			task_map: taskmapImageData,
			..._.pick(task, ['created_at', 'expires_at', 'closed_at', 'flg_approved']),
			activities: activities
					?.filter((a) => !a.flg_disabled)
					?.map((a) => ({
						id: a.id,
						title: a.val_text,
						description: a.val_description,
						flg_custom: a.activity_type === 'custom',
						closed_at: a.closed_at,
						closed_by: a.val_closed_by || null,
					}))
				?? [],
		}
		return await dispatchPDFDownload(data, true, null);
	}


	/*
	getTaskImageName is a function that gets the name of a task image
	parameters:
	- taskImage: the task image
	- worksitesActivityAttachmentCounter: a counter to keep track of the attachments with no name
	returns the name of the task image
	 */
	const getTaskImageName = (taskImage, worksitesActivityAttachmentCounter) => {
		const extension = taskImage.val_url.split(".").pop();
		const filename = (taskImage.val_description? `${taskImage.val_description}.${extension}`:
			`${intl.formatMessage({id: 'attachment'})} ${worksitesActivityAttachmentCounter}.${extension}`)
		return filename
	}

	let worksitesActivityAttachmentCounter = 0
	return {
		title: intl.formatMessage({id:'tasks'}),
		alternateColor: "bg-gray-100",
		groups: [
			{
				key: "task_images",
				title: `${intl.formatMessage({id:'maps'})} ${intl.formatMessage({id:'and'})} ${intl.formatMessage({id:'attachments'})}`,
				/*
				each document has:
				- a name -> the image name
				- s3_url -> the s3 url of the image
				- bucket -> used by the backend to know the proper bucket
				- what to do when the document is clicked
				- info -> loaded on ... at ... by ... (promise to be resolved see the method buildTaskImageInfo)
				- selected -> whether the document is selected (checkbox clicked)
				- visible -> whether the document is visible (matches the search filter)
				 */
				documents: taskImages?.map((taskImage, index) => {
					if (taskImage.id_worksites_activity) {
						worksitesActivityAttachmentCounter++
					}
					const filename = getTaskImageName(taskImage, worksitesActivityAttachmentCounter)
					return {
						name: filename,
						s3_url: taskImage.val_url,
						bucket: 'worksite_images',
						onDocumentClicked: async () => {
							const signed_url = await getSignedUrl(apiRequest, taskImage.val_url, filename, 'worksite_images');
							await downloadFileAndShowInTab(signed_url, filename);
						},
						info: buildTaskImageInfo(taskImage),
						selected: false,
						visible: true
					}
				})
			},
			{
				key: "task_reports",
				title: `${intl.formatMessage({id:'Report'})}`,
				/*
				each document has:
				- a name -> the task name
				- what to do when the document is clicked
				- task -> the task
				- selected -> whether the document is selected (checkbox clicked)
				- visible -> whether the document is visible (matches the search filter)
				 */
				documents: tasks?.map(task => {
					return {
						name: task.des_name,
						onDocumentClicked: async () => {
							await downloadTaskReport(
								task,
								(response) => {
								// once the report is downloaded, open it in a new tab
								const url = URL.createObjectURL(response);
								window.open(url, '_blank');
							});
						},
						task: task,
						selected: false,
						visible: true
					}
				})
			}
		],
		actions: {
			download: {
				actionComponent: (documents, visible) => <ActionButtonComponent
					icon={ArrowDownTrayIcon}
					action={async () => {
						await prepareAndDownloadZip(documents)
					}}
				/>
			}
		}
		// /*
		// actions user can do on all the documents of the section
		//  */
		// onAllActions: [
		// 	{
		// 		actionComponent: (documents, disabled) => <ActionButtonComponent
		// 			icon={ArrowDownTrayIcon}
		// 			action={async () => {
		// 				await prepareAndDownloadZip(documents)
		// 			}}
		// 			disabled={disabled}
		// 		/>
		// 	}
		// ],
		// /*
		// actions user can do on the selected documents of the section
		//  */
		// onSelectedActions: [
		// 	{
		// 		actionComponent: (documents) => <ActionButtonComponent
		// 			icon={ArrowDownTrayIcon}
		// 			action={async () => {
		// 				await prepareAndDownloadZip(documents)
		// 			}}
		// 			text={intl.formatMessage({id: "download"})}
		// 			styleType={"white"}
		// 		/>
		// 	}
		// ]
	}
}